import { supportMailto } from '@/modules/support/support';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/modules/ui/components/alert/Alert';
import { Button } from '@/modules/ui/components/button/Button';

interface HMLoadErrorProps {
  description?: string;
  isRetrying?: boolean;
  onRetry?: () => void;
  /**
   * Adds an email-support exit, prefilled with this subject. Set it where
   * retrying cannot help, so the error is never the end of the road.
   */
  supportSubject?: string;
  title?: string;
}

const defaultDescription =
  'Something went wrong on our side. Please try again.';

/**
 * Shown when the hiring-manager data cannot be loaded. Without it a failed
 * query looks like an empty account: the layout would render the onboarding
 * screen and the Jobs page would say no decisions are waiting.
 */
export function HMLoadError({
  description = defaultDescription,
  isRetrying = false,
  onRetry,
  supportSubject,
  title,
}: HMLoadErrorProps) {
  return (
    <Alert variant="destructive">
      {title && <AlertTitle>{title}</AlertTitle>}
      <AlertDescription className="gap-3">
        <span>{description}</span>
        {(onRetry || supportSubject) && (
          <div className="flex flex-wrap gap-2">
            {onRetry && (
              <Button
                loading={isRetrying}
                onClick={onRetry}
                size="sm"
                variant="outline"
              >
                Try again
              </Button>
            )}
            {supportSubject && (
              <Button asChild size="sm" variant="outline">
                <a
                  href={supportMailto(supportSubject)}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Email support
                </a>
              </Button>
            )}
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}
